/* eslint-disable react/prop-types */
import { useState, useRef } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"
import { ColorSelector } from "./color-selector"
import { SizeSelector } from "./size-selector"
import { SpecsSection } from "./specs-section"
import { BikeViewer } from "./bike-viewer"
import { AddToCartModal } from "./addToCartModal"
import { ShoppingCart } from "lucide-react"
import { useProductos } from "../../hooks/useProductos"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"

import axios from "axios";
import toast, { Toaster } from "react-hot-toast"
import { Try } from "@mui/icons-material"
import { InsertPageBreakOutlined } from "@mui/icons-material"
import { useHome } from "../../contexts/HomeContext"

const Container = styled.div`
  min-height: 100vh;
  color: #ffffff;
  overflow-x: hidden;
`

const Hero = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, transparent 0%, #000000 100%);
    pointer-events: none;
  }
`

const ContentSection = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  position: relative;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 2rem 1rem;
    gap: 2rem;
  }
`

const ProductInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  order: 1;

  @media (max-width: 1024px) {
    order: 2;
  }
`

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin: 0;
  background: linear-gradient(to right, #ffffff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`

const Price = styled(motion.div)`
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  display: inline-block;

  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 1rem 1.5rem;
  }
`

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const Button = styled(motion.button)`
  padding: 1rem 2rem;
  border-radius: 30px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &.primary {
    background: linear-gradient(135deg, #00ff88, #00cc6f);
    color: #000000;
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    backdrop-filter: blur(10px);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`

const ParallaxBg = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('https://images.unsplash.com/photo-1533740566848-5f7d3e04e3d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80') no-repeat center center;
  background-size: cover;
  z-index: -1;
`

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`

const SelectorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`

const SelectorWrapper = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`

const SelectorLabel = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`

const PriceAndCartContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`


export default function BikeProduct({ bike }) {
  const [selectedColor, setSelectedColor] = useState(null)
  const [selectedSize, setSelectedSize] = useState(null) // Default to 'M'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const containerRef = useRef(null)
  const { featuredProduct, isLoading } = useHome()
  const [product, setProduct] = useState()
  const [productImage, setProductImage] = useState(null)
  const { productos, formatoCOP, addCart } = useProductos();
  const [bikeTallas, setBikesTallas] = useState([])
  const [colors, setColors] = useState([])

  const navigate = useNavigate()
  const ordenarTallas = (tallas) => {
    return [...new Set(tallas)].sort((a, b) => {
      // Extraer el número inicial y el rango de altura
      const [aNum] = a.match(/^\d+/) || ['0'];
      const [bNum] = b.match(/^\d+/) || ['0'];
      
      // Convertir a números para comparación
      const numA = parseInt(aNum);
      const numB = parseInt(bNum);

      if (numA !== numB) {
        return numA - numB;
      }

      // Si los números son iguales, ordenar por el rango de altura
      const getAltura = (str) => {
        const match = str.match(/\d+\.\d+/g);
        return match ? parseFloat(match[match.length - 1]) : 0;
      };

      const alturaA = getAltura(a);
      const alturaB = getAltura(b);

      return alturaA - alturaB;
    });
  };

  useEffect(() => {
    if (!featuredProduct) return
    setProduct(featuredProduct.productable)

    setProductImage(product ? product?.inventories[0]?.images?.find(img => img.is_main == 1 || true)?.pathname || product?.inventories[0]?.images[0]?.pathname : [])
    
    // Filtrar tallas duplicadas y ordenarlas
    const tallasUnicas = product?.inventories?.map(inv => inv.short_size) || [];
    setBikesTallas(ordenarTallas(tallasUnicas));
    
    // Filtrar colores duplicados
    const coloresUnicos = [...new Set(product?.inventories?.map(inv => inv.color_hex) || [])];
    setColors(coloresUnicos);
  }, [featuredProduct, bike, isLoading, product])

  useEffect(() => {
    if (!featuredProduct) return
    try {
      setSelectedSize(null)
      // Filtrar y ordenar tallas para el color seleccionado
      const tallasFilters = product.inventories
        .filter(objeto => objeto.color_hex === selectedColor)
        .map(objeto => objeto.short_size);
      
      // Eliminar duplicados y ordenar las tallas
      const tallasUnicasOrdenadas = ordenarTallas(tallasFilters);
      setBikesTallas(tallasUnicasOrdenadas);

      // Actualizar la imagen
      const selected = product.inventories.find(img => img.color_hex === selectedColor);
      if (selected) {
        const mainImage = selected.images.find(img => img.is_main === 1)?.pathname;
        setProductImage(mainImage || selected.images[0]?.pathname);
      }
    } catch (error) {
      console.log(error)      
    }
  }, [selectedColor])

  const handleAddCart = async () => {
    if (!selectedColor) return toast.error("Selecciona un color")
    if (!selectedSize) return toast.error("Selecciona una talla")
    async function getIP() {
       let ip = '0.0.0.0'; // Valor por defecto
                console.log(localStorage.getItem('user_ip'))
                if (localStorage.getItem('user_ip')){
                ip = localStorage.getItem("user_ip")
                return ip
            }
            else{
                const { ip: fetchedIp } = (await axios.get("https://api.ipify.org?format=json")).data;
                ip = fetchedIp;
                localStorage.setItem("user_ip", ip);
                return InsertPageBreakOutlined
            }
    }
    
    async function showIP() {
      const data = await getIP();
      addCart({ ...product, type: featuredProduct?.type == "bicycle" ? "bike" : "scooter" , cantidad: 1, id_usuario: undefined, size: selectedSize, ip: data, color: selectedColor })
      console.log(product)
    }

    showIP()
    setIsModalOpen(true)
  }

  const handleAddToCart = async () => {
    await handleAddCart()
  }

  return (
    <Container ref={containerRef}>
      <Toaster></Toaster>
      <ContentSection>
        <BikeViewer text={featuredProduct ? featuredProduct?.badge : ""} price={featuredProduct ? formatoCOP.format(product?.price) : ""} img={productImage}/>
        <ProductInfo
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <ProductDetails>
            <motion.div>
              <h2>{product?.name}</h2>
              <p>
                {product?.product_description}
              </p>
            </motion.div>

            <SelectorsContainer>
              <SelectorWrapper>
                <SelectorLabel>Color</SelectorLabel>
                <ColorSelector colors={colors} activeColor={selectedColor} onColorSelect={setSelectedColor} />
              </SelectorWrapper>
              <SelectorWrapper>
                <SelectorLabel>Talla</SelectorLabel>
                <SizeSelector sizes={bikeTallas} activeSize={selectedSize} onSizeSelect={setSelectedSize} />
              </SelectorWrapper>
            </SelectorsContainer>

            <PriceAndCartContainer>
              {/* <Price
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {formatoCOP.format(product?.precio)}
              </Price> */}

              <ButtonGroup
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Button
                  className="primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddToCart}
                >
                  <ShoppingCart size={20} />
                  Añadir al carrito
                </Button>
                <Button className="secondary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/bicicletas')}>
                  {/* <Heart size={20} /> */}
                  Ver Mas
                </Button>
              </ButtonGroup>
            </PriceAndCartContainer>
          </ProductDetails>

          <SpecsSection specs={product ? [["frenos", product.details.frenos], ["marco", product.details.marco], ["casette", product.details.cassette], ["peso", product.details.peso]] : []}/>
        </ProductInfo>
      </ContentSection>

      <AddToCartModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} name={product?.name}/>
    </Container>
  )
}

